/**
 * Signal display names — shared client-side resolver.
 * ====================================================
 *
 * Users can rename any signal key to a friendly display name ("o" -> "Open")
 * from the ingestion mapping UI or the Data Signals page. List endpoints
 * (GET /signal, /segment/workspace-analysis) already return `display_name`
 * on their rows; surfaces that only hold RAW KEY STRINGS (discovery briefs,
 * relationship graphs, model feature chips, deployment feature lists...) use
 * this hook instead: it fetches the workspace's {key: display_name} map once
 * (GET /signal/display-names), caches it per workspace, and resolves keys —
 * including entity-suffixed children (`base__AAPL` -> "Open · AAPL", matching
 * the backend's resolution rule).
 */

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import config from '../config';
import { getLocalUser } from './localUser';

// Per-workspace cache so N components on one page share a single fetch.
const _cache = {};      // workspaceId -> {key: display_name}
const _inflight = {};   // workspaceId -> Promise

const _workspaceId = () => {
  try { return localStorage.getItem('WORKSPACE_ID') || ''; } catch (_e) { return ''; }
};

export const fetchDisplayNames = async () => {
  const ws = _workspaceId();
  if (_cache[ws]) return _cache[ws];
  if (!_inflight[ws]) {
    _inflight[ws] = axios
      .get(`${config.API_URL}/signal/display-names`, { headers: getLocalUser() })
      .then((res) => {
        // responseJSON wraps payloads as {data: {data: <map>}}.
        const body = res.data?.data ?? {};
        const map = (body.data && typeof body.data === 'object') ? body.data : body;
        _cache[ws] = (map && typeof map === 'object') ? map : {};
        return _cache[ws];
      })
      .catch(() => {
        _cache[ws] = {};
        return _cache[ws];
      })
      .finally(() => { delete _inflight[ws]; });
  }
  return _inflight[ws];
};

/** Invalidate the cache (call after a rename) so the next mount refetches. */
export const invalidateDisplayNames = () => {
  const ws = _workspaceId();
  delete _cache[ws];
};

/**
 * Resolve one key against a names map. Exact match wins; otherwise an
 * entity-suffixed child (`base__entity`) of a named base key resolves to
 * "<base name> · <entity>" (longest base wins). Returns null when nothing
 * matches so callers can fall back to the raw key.
 */
export const resolveDisplayName = (key, names) => {
  if (!key || !names) return null;
  if (names[key]) return names[key];
  let bestBase = null;
  Object.keys(names).forEach((base) => {
    if (key.startsWith(`${base}__`)
        && (bestBase === null || base.length > bestBase.length)) {
      bestBase = base;
    }
  });
  if (bestBase) {
    const suffix = key.slice(bestBase.length + 2);
    return suffix ? `${names[bestBase]} · ${suffix}` : names[bestBase];
  }
  return null;
};

/**
 * Hook: returns a STABLE `(key) => friendlyName` for the active workspace
 * (safe to use in effect/memo dependency arrays). Renders raw keys until
 * the map arrives, then re-renders with names.
 */
export const useDisplayNames = () => {
  const [names, setNames] = useState(() => _cache[_workspaceId()] || null);

  useEffect(() => {
    let alive = true;
    fetchDisplayNames().then((map) => { if (alive) setNames(map); });
    return () => { alive = false; };
  }, []);

  return useCallback(
    (key) => resolveDisplayName(key, names) || key,
    [names],
  );
};

export default useDisplayNames;
